import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/Header";
import Intro from "./Intro";
import About from "./About";
import Experiences from "./Experiences";
import Projects from "./Projects";
import Contact from "./Contact";
import ContactForm from "./Contact_me";
import Footer from "./Footer";
import LeftSider from "./LeftSider";

function Home() {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [scrollY, setScrollY] = useState(0);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Enhanced star field animation
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      // Star particles with varying sizes
      let stars = [];
      const shootingStars = [];

      // Function to create stars
      const createStars = () => {
        stars = [];
        // Increase star count and ensure even distribution
        const starCount = Math.floor((canvas.width * canvas.height) / 5000);
        for (let i = 0; i < starCount; i++) {
          stars.push({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            radius: Math.random() * 1.5 + 0.3,
            opacity: Math.random() * 0.6 + 0.3,
            twinkleSpeed: Math.random() * 0.015 + 0.003,
            twinklePhase: Math.random() * Math.PI * 2,
            layer: Math.random(), // for parallax
          });
        }
      };

      // Create initial stars
      createStars();

      const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        createStars(); // Regenerate stars on resize
      };
      window.addEventListener("resize", handleResize); // Animation loop
      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw and animate stars
        stars.forEach((star) => {
          star.twinklePhase += star.twinkleSpeed;
          const twinkle = Math.sin(star.twinklePhase) * 0.3 + 0.7;

          ctx.beginPath();
          ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * twinkle})`;
          ctx.fill();

          // Add glow to larger stars
          if (star.radius > 1.2) {
            const gradient = ctx.createRadialGradient(
              star.x,
              star.y,
              0,
              star.x,
              star.y,
              star.radius * 3
            );
            gradient.addColorStop(
              0,
              `rgba(255, 255, 255, ${star.opacity * twinkle * 0.3})`
            );
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
            ctx.fillStyle = gradient;
            ctx.fillRect(
              star.x - star.radius * 3,
              star.y - star.radius * 3,
              star.radius * 6,
              star.radius * 6
            );
          }
        });

        // Shooting stars
        if (Math.random() < 0.01 && shootingStars.length < 3) {
          shootingStars.push({
            x: Math.random() * canvas.width,
            y: (Math.random() * canvas.height) / 2,
            length: Math.random() * 100 + 50,
            speed: Math.random() * 8 + 4,
            opacity: 1,
            angle: (Math.random() * Math.PI) / 4 + Math.PI / 4,
          });
        }

        // Draw shooting stars
        shootingStars.forEach((star, index) => {
          ctx.beginPath();
          ctx.moveTo(star.x, star.y);
          ctx.lineTo(
            star.x - Math.cos(star.angle) * star.length,
            star.y - Math.sin(star.angle) * star.length
          );
          ctx.strokeStyle = `rgba(255, 255, 255, ${star.opacity})`;
          ctx.lineWidth = 2;
          ctx.stroke();

          // Add glow
          const gradient = ctx.createLinearGradient(
            star.x,
            star.y,
            star.x - Math.cos(star.angle) * star.length,
            star.y - Math.sin(star.angle) * star.length
          );
          gradient.addColorStop(
            0,
            `rgba(200, 220, 255, ${star.opacity * 0.6})`
          );
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 4;
          ctx.stroke();

          // Update position
          star.x += Math.cos(star.angle) * star.speed;
          star.y += Math.sin(star.angle) * star.speed;
          star.opacity -= 0.01;

          // Remove if faded
          if (star.opacity <= 0) {
            shootingStars.splice(index, 1);
          }
        });

        animationRef.current = requestAnimationFrame(animate);
      };

      animate();
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="relative bg-gradient-to-b from-black via-slate-950 to-black text-white overflow-hidden">
      {/* Animated star field canvas */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0 opacity-80"
      />

      {/* Animated nebulas - more subtle and refined */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-20 right-20 w-[900px] h-[900px] bg-gradient-to-br from-purple-600/[0.05] to-blue-600/[0.03] blur-[200px] rounded-full animate-nebula-1"></div>
        <div className="absolute bottom-20 left-40 w-[800px] h-[800px] bg-gradient-to-tr from-blue-500/[0.04] to-cyan-500/[0.02] blur-[180px] rounded-full animate-nebula-2"></div>
        <div className="absolute top-1/2 left-1/3 w-[700px] h-[700px] bg-gradient-to-bl from-cyan-400/[0.03] to-purple-500/[0.02] blur-[160px] rounded-full animate-nebula-3"></div>
      </div>

      {/* Enhanced space-time grid with parallax and mouse distortion */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-20"
        style={{
          perspective: "1500px",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          className="absolute inset-0 transition-all duration-300 ease-out"
          style={{
            backgroundImage: `
              linear-gradient(rgba(100,150,255,0.12) 1.5px, transparent 1.5px),
              linear-gradient(90deg, rgba(100,150,255,0.12) 1.5px, transparent 1.5px)
            `,
            backgroundSize: "80px 80px",
            transform: `
              perspective(1200px) 
              rotateX(60deg) 
              translateY(-25%) 
              translateZ(${scrollY * -0.1}px)
              scale(2.8)
              translate(${(mousePosition.x - 50) * 0.6}px, ${
              (mousePosition.y - 50) * 0.6
            }px)
            `,
            transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
            filter: `
              blur(${Math.abs(mousePosition.x - 50) / 20}px)
              brightness(${1 + Math.abs(mousePosition.x - 50) / 250})
            `,
          }}
        />
      </div>

      {/* Cursor glow effect - more pronounced */}
      <div
        className="fixed inset-0 pointer-events-none z-0 transition-opacity duration-500"
        style={{
          background: `
            radial-gradient(circle 600px at ${mousePosition.x}% ${mousePosition.y}%, 
            rgba(100, 150, 255, 0.06), 
            rgba(80, 120, 255, 0.02) 40%,
            transparent 70%)
          `,
          opacity: 0.8,
        }}
      />

      {/* Subtle vignette for depth */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/40"></div>
      </div>

      {/* Refined scanlines - barely visible */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.015]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, transparent 1px, transparent 3px)",
            animation: "scan 10s linear infinite",
          }}
        ></div>
      </div>

      {/* Header */}
      <Header />

      {/* Left Social Sidebar */}
      <LeftSider />

      {/* Main Content Wrapper */}
      <main className="relative z-10">
        {/* HERO */}
        <section id="home">
          <Intro />
        </section>

        {/* ABOUT */}
        <section id="about">
          <About />
        </section>

        {/* EXPERIENCE */}
        <section id="experience">
          <Experiences />
        </section>

        {/* PROJECTS */}
        <section id="projects">
          <Projects />
        </section>

        {/* SAY HELLO */}
        <section id="say-hello">
          <Contact />
        </section>

        {/* CONTACT FORM */}
        <section id="contact">
          <ContactForm />
        </section>
      </main>

      {/* Footer */}
      <Footer />

      <style>{`
        @keyframes nebula-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.04; }
          50% { transform: translate(50px, -30px) scale(1.1); opacity: 0.06; }
        }

        @keyframes nebula-2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.03; }
          50% { transform: translate(-40px, 40px) scale(1.15); opacity: 0.05; }
        }

        @keyframes nebula-3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.025; }
          50% { transform: translate(30px, 50px) scale(1.08); opacity: 0.04; }
        }

        @keyframes scan {
          0% { transform: translateY(0); }
          100% { transform: translateY(100px); }
        }

        .animate-nebula-1 { animation: nebula-1 20s ease-in-out infinite; }
        .animate-nebula-2 { animation: nebula-2 25s ease-in-out infinite; }
        .animate-nebula-3 { animation: nebula-3 30s ease-in-out infinite; }

        :global(html) {
          scroll-behavior: smooth;
        }

        :global(::-webkit-scrollbar) {
          width: 8px;
        }

        :global(::-webkit-scrollbar-track) {
          background: rgba(0, 0, 0, 0.8);
        }

        :global(::-webkit-scrollbar-thumb) {
          background: rgba(100, 150, 255, 0.2);
          border-radius: 4px;
        }

        :global(::-webkit-scrollbar-thumb:hover) {
          background: rgba(100, 150, 255, 0.3);
        }

        section {
          position: relative;
        }
      `}</style>
    </div>
  );
}

export default Home;
